import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import type { ListEntry, OrgMember, Organization } from '@heybesafe/shared';
import { moveEntry } from '@heybesafe/shared';
import { useSafe } from '../../context/SafeContext';
import { useFamilyCategories } from '../../hooks/useFamilyCategories';
import { useFamilyKeys } from '../../context/FamilyKeysContext';
import { useMemberNames, memberLabel } from '../../hooks/useMemberNames';
import { getOrg, getCurrentUserId } from '../../api/client';
import LoadingPane from '../../components/LoadingPane';
import ListChecklist from '../../components/ListChecklist';
import { HbsButton } from '../../components/hbs/Button';

/**
 * Family list (collaborative checklist) detail. Any family member can add,
 * remove, and check items here; each item shows who added it and when. Edits
 * persist through `updateItem` (debounced push), and the shared per-entry merge
 * reconciles concurrent edits from other members on sync — so two people adding
 * at once never clobber each other. See the Binder-lists spec.
 */
export default function ListDetailPage(): JSX.Element {
  const { orgId, categoryId, itemId } = useParams<{ orgId: string; categoryId: string; itemId: string }>();
  const navigate = useNavigate();
  const { items, itemsLoaded, orgs = [], updateItem, sync } = useSafe();
  const categoriesByOrg = useFamilyCategories();
  const keys = useFamilyKeys();

  // Live convergence: while a list is open, poll `sync()` so another member's
  // adds/checks appear (and merge) without a reload. Sync is pull-merge-push, so
  // an idle viewer just pulls the server union; an editor's local entries ride
  // along. Scoped to the mounted detail view to keep it light.
  useEffect(() => {
    const id = window.setInterval(() => { void sync(); }, 6000);
    return () => window.clearInterval(id);
  }, [sync]);

  const item = items.find((i) => i.id === itemId && i.orgId === orgId && i.type === 'list');
  const org = orgs.find((o) => o.id === orgId);
  const category = (orgId ? categoriesByOrg.get(orgId) ?? [] : []).find((c) => c.categoryId === (item?.categoryId ?? categoryId));
  const backTo = `/orgs/${orgId}/binder/${categoryId}`;

  // Member roster for attribution. Fetched once; `members` is held in state so
  // its reference is stable for useMemberNames (which re-decrypts on identity change).
  const [members, setMembers] = useState<OrgMember[]>([]);
  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    getOrg(orgId).then((r) => { if (!cancelled && r.ok) setMembers(r.data.members); });
    return () => { cancelled = true; };
  }, [orgId]);
  const names = useMemberNames(orgId, members, org as Pick<Organization, 'publicKey'> | undefined, keys);
  const currentUserId = getCurrentUserId() ?? '';

  // Local, per-list, per-surface "show done" toggle — never synced to the server.
  const showDoneKey = `hbs.safe.list.${orgId}.${itemId}.showDone`;
  const [showDone, setShowDone] = useState<boolean>(() => {
    try { return localStorage.getItem(showDoneKey) !== '0'; } catch { return true; }
  });
  function toggleShowDone(): void {
    setShowDone((v) => {
      const next = !v;
      try { localStorage.setItem(showDoneKey, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }

  function memberLabelFor(userId: string): string {
    const m = members.find((mm) => mm.userId === userId);
    if (m) return memberLabel(m, names);
    if (userId && userId === currentUserId) return 'You';
    return 'A family member';
  }

  if (!item) {
    if (!itemsLoaded) return <LoadingPane label="Loading list…" />;
    return (
      <div className="h-full flex flex-col items-center justify-center text-hbs-text-mute gap-3">
        <p className="text-[14px]">List not found.</p>
        <HbsButton variant="plain" id="list-not-found-back-button" onClick={() => navigate(backTo)}>Back</HbsButton>
      </div>
    );
  }

  function persist(next: ListEntry[]): void {
    if (!item) return;
    void updateItem(item.id, { type: 'list', listItems: next });
  }

  function move(entryId: string, prevId: string | null, nextId: string | null): void {
    if (!item) return;
    const moved = moveEntry(item, entryId, prevId, nextId, currentUserId);
    void updateItem(item.id, { type: 'list', listItems: moved.listItems });
  }

  return (
    <div className="p-7 flex flex-col gap-6 max-w-3xl">
      <div>
        <p className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">
          {org?.name ?? 'Family'}{category ? ` · ${category.name}` : ''}
        </p>
        <h1 className="font-display font-semibold text-[30px] tracking-hbs-tighter text-hbs-text">
          {item.name}
        </h1>
        <Link
          to={`/orgs/${orgId}/binder/${categoryId}/lists/${item.id}/edit`}
          className="text-[13px] font-semibold text-hbs-primary hover:underline"
        >
          Rename
        </Link>
      </div>

      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6 flex flex-col gap-2">
        <div className="flex justify-end">
          <button
            type="button"
            id="list-show-done-toggle"
            onClick={toggleShowDone}
            className="text-[12px] font-semibold text-hbs-primary hover:underline"
            aria-pressed={!showDone}
          >
            {showDone ? 'Hide done' : 'Show done'}
          </button>
        </div>
        <ListChecklist
          entries={item.listItems ?? []}
          currentUserId={currentUserId}
          memberLabelFor={memberLabelFor}
          showDone={showDone}
          onChange={persist}
          onMove={move}
        />
      </section>

      <div>
        <HbsButton variant="plain" id="list-back-to-category-button" onClick={() => navigate(backTo)}>
          Back to {category?.name ?? 'category'}
        </HbsButton>
      </div>
    </div>
  );
}
